import { useState } from "react";
import { format } from "date-fns";
import { MessageCircle, CalendarDays, Settings, Phone, Mail, MapPin, Check, Droplet } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import PageHero, { SplitSection } from "@/components/site/PageHero";
import LeadForm from "@/components/site/LeadForm";
import { Btn, Reveal } from "@/components/site/Reveal";
import { BRAND, TIMES } from "@/content";
import { scrollToId } from "@/lib/scroll";

const MINI = [
  { icon: MessageCircle, title: "Get Answers", text: "Talk with our team about your water needs" },
  { icon: CalendarDays, title: "Schedule a Visit", text: "Book a free in-home water test" },
  { icon: Settings, title: "Find the Right System", text: "Get personalized recommendations" },
];

const AREAS = ["San Diego", "East County", "North County", "South Bay", "Coastal Communities", "Surrounding Areas"];

const ContactInfo = () => (
  <div data-testid="contact-info" className="mt-8 space-y-6">
    {[
      { icon: Phone, title: "Call Us", main: BRAND.phone, href: BRAND.phoneHref, sub: BRAND.hours },
      { icon: Mail, title: "Email Us", main: BRAND.email, href: `mailto:${BRAND.email}`, sub: "We typically respond within 1 business day" },
      { icon: MapPin, title: "Service Area", main: "San Diego County, CA", sub: "In-home water testing and consultation available throughout the San Diego area." },
    ].map((c) => (
      <div key={c.title} className="flex gap-5">
        <c.icon className="h-9 w-9 shrink-0" strokeWidth={1.3} />
        <div>
          <p className="font-head font-semibold text-[16px]">{c.title}</p>
          {c.href ? <a href={c.href} className="text-[15px] hover:underline">{c.main}</a> : <p className="text-[15px]">{c.main}</p>}
          <p className="text-[13px] text-[var(--body)] mt-0.5">{c.sub}</p>
        </div>
      </div>
    ))}
  </div>
);

const Scheduler = ({ date, setDate, time, setTime }) => (
  <div data-testid="scheduler" className="bg-white border border-[var(--line)] grid sm:grid-cols-[1fr_130px]">
    <div className="p-4 sm:border-r border-[var(--line)]">
      <p className="font-head font-semibold text-[14px] mb-2">Select a Date</p>
      <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} className="p-0" classNames={{ day_selected: "!rounded-full bg-[var(--ink)] text-white hover:bg-[var(--ink)]", day_today: "font-bold underline" }} />
    </div>
    <div className="p-4">
      <p className="font-head font-semibold text-[14px] mb-2">Select a Time</p>
      <div className="grid grid-cols-4 sm:grid-cols-1 gap-2">
        {TIMES.map((t) => (
          <button key={t} type="button" onClick={() => setTime(t)} data-testid={`time-${t.replace(/[^0-9APM]/g, "")}`} className={`border py-1.5 text-[12px] transition-colors ${time === t ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--ink)]"}`}>{t}</button>
        ))}
      </div>
    </div>
  </div>
);

export default function Contact() {
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const dateStr = date ? format(date, "EEE, MMM d, yyyy") : "";

  return (
    <>
      <PageHero
        testId="contact-hero"
        eyebrow="Contact & Book a Consultation"
        lines={["We're here", "to help."]}
        text="Have questions, want to schedule a free water test, or need help choosing the right system? Our team is ready to help you get cleaner, healthier water for your home."
        img="/img/tech-door.jpg"
        alt="Water System Bros. technician arriving at a home"
        features={MINI}
      />
      <section data-testid="contact-booking" className="bg-[var(--soft)] py-14 lg:py-16">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 lg:gap-0">
          <Reveal className="bg-white p-6 sm:p-8 lg:mr-6">
            <p className="eyebrow">Request a Consultation</p>
            <h2 className="headline mt-3 text-3xl sm:text-4xl">Book a free water test</h2>
            <p className="mt-3 mb-7 text-[16px] text-[var(--body)]">Fill out the form below and we'll reach out to schedule a convenient time for your in-home water test or consultation.</p>
            <LeadForm kind="consultation" date={dateStr} time={time} onDone={() => { setDate(undefined); setTime(""); }} />
          </Reveal>
          <Reveal delay={0.1} className="lg:border-l border-[#D4D4D4] lg:pl-6">
            <p className="eyebrow">Prefer to Schedule Directly?</p>
            <h2 className="headline mt-3 text-3xl sm:text-4xl">Choose a time<br />that works for you.</h2>
            <p className="mt-3 mb-6 text-[16px] text-[var(--body)]">Pick a date and time — it's added to your request automatically.</p>
            <Scheduler date={date} setDate={setDate} time={time} setTime={setTime} />
            <ContactInfo />
          </Reveal>
        </div>
      </section>
      <SplitSection testId="service-area" reverse eyebrow="Our Service Area" title={<>Proudly serving<br />San Diego County.</>} text="We provide free in-home water testing and consultations throughout San Diego County, including coastal, inland, and surrounding areas." img="/img/sd-bay.jpg" alt="San Diego bay and skyline">
        <ul className="mt-7 grid grid-cols-2 gap-y-3 gap-x-6 max-w-md">
          {AREAS.map((a) => <li key={a} className="flex items-center gap-3 text-[15px] text-[var(--body)]"><Check className="h-4 w-4 text-[var(--ink)]" strokeWidth={2.5} />{a}</li>)}
        </ul>
      </SplitSection>
      <section data-testid="contact-cta" className="bg-[var(--soft)] py-12">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-[auto_1fr_auto] gap-8 items-center">
          <Reveal className="flex items-center gap-6"><Droplet className="h-14 w-14" strokeWidth={1.2} /><h2 className="headline text-3xl sm:text-4xl">Cleaner water<br />starts here.</h2></Reveal>
          <Reveal delay={0.1} className="lg:border-l border-[#D4D4D4] lg:pl-8"><p className="text-[16px] text-[var(--body)] max-w-md">Take the first step toward better tasting, healthier water for you and your family. Contact us or book a free water test today.</p></Reveal>
          <Reveal delay={0.2}><Btn onClick={() => scrollToId("contact-booking")} testId="contact-cta-btn">Request a Free Water Test</Btn></Reveal>
        </div>
      </section>
    </>
  );
}
